'use client';
import { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Building2, FileText, Bell,
  Settings, LogOut, Shield, UserCircle, Briefcase, ChevronUp,
  Menu, X, BarChart2, Network, Mail, ShieldCheck, Columns3, Building,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import { APP_VERSION, LAST_UPDATED } from '@/version';

const navBroker = [
  { href: '/dashboard',                                  label: 'Dashboard',        icon: LayoutDashboard },
  { href: '/dashboard/clienti',                          label: 'Clienti',          icon: Users },
  { href: '/dashboard/pratiche',                         label: 'Pratiche',         icon: FileText },
  { href: '/dashboard/kanban',                           label: 'Stato Pratiche',   icon: Columns3 },
  { href: '/dashboard/broker-procacciatori',             label: 'Rete broker',      icon: Network },
  { href: '/dashboard/istituti',                         label: 'Istituti',         icon: Building2 },
  { href: '/dashboard/impostazioni/template-email',      label: 'Template email',   icon: Mail },
  { href: '/dashboard/impostazioni/gdpr',                label: 'GDPR',             icon: ShieldCheck },
  { href: '/dashboard/notifiche',                        label: 'Notifiche',        icon: Bell },
];

const navAdmin = [
  { href: '/dashboard/admin',              label: 'Statistiche',    icon: LayoutDashboard },
  { href: '/dashboard/admin/tenants',      label: 'Tenant',         icon: Briefcase },
  { href: '/dashboard/admin/utenti',       label: 'Tutti gli utenti', icon: Users },
  { href: '/dashboard/admin/piani',        label: 'Piani abbonamento', icon: FileText },
  { href: '/dashboard/admin/costi-api',    label: 'Costi API',      icon: BarChart2 },
];

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const pathname  = usePathname();
  const router    = useRouter();
  const { utente, clearAuth } = useAuthStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/auth/logout', { refreshToken });
    } catch { /* ignora */ }
    clearAuth();
    toast.success('Logout effettuato');
    router.push('/login');
  };

  const isSuperadmin = utente?.ruolo === 'superadmin';
  const hasTenant = !!utente?.tenantId;
  const orgItem = { href: '/dashboard/organizzazione', label: 'La mia organizzazione', icon: Building };
  // Superadmin CON un'organizzazione collegata = doppio cappello: vede sia il menu
  // operativo (+ "La mia organizzazione") sia il pannello di amministrazione piattaforma.
  const nav =
    isSuperadmin && hasTenant ? [...navBroker, orgItem, ...navAdmin]
    : isSuperadmin ? navAdmin
    : utente?.ruolo === 'admin' ? [...navBroker, orgItem]
    : navBroker;
  const sezione =
    isSuperadmin && hasTenant ? 'Gestione e Amministrazione'
    : isSuperadmin ? 'Amministrazione'
    : 'Gestione';

  return (
    <>
      {/* Overlay mobile */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-30 flex flex-col bg-white dark:bg-slate-900
          border-r border-border transition-all duration-200
          ${collapsed ? '-translate-x-full lg:translate-x-0 lg:w-16' : 'translate-x-0 w-64'}
          lg:static lg:z-auto
        `}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border h-12 shrink-0">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-xs">FB</span>
          </div>
          {!collapsed && (
            <span className="font-semibold text-sm truncate">ApplyBank</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {!collapsed && (
            <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              {sezione}
            </p>
          )}
          {nav.map((item) => {
            const active = pathname === item.href;
            return (
              <button
                key={item.href}
                onClick={() => router.push(item.href)}
                className={`
                  w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm font-medium transition-colors
                  ${active
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                `}
              >
                <item.icon className="size-4 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Footer utente */}
        <div className="border-t border-border p-2 shrink-0">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-accent transition-colors text-sm"
            >
              <UserCircle className="size-4 shrink-0 text-muted-foreground" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left truncate">
                    {utente?.nome} {utente?.cognome}
                  </span>
                  <ChevronUp className={`size-3 opacity-50 transition-transform ${userMenuOpen ? '' : 'rotate-180'}`} />
                </>
              )}
            </button>

            {userMenuOpen && (
              <div className="absolute bottom-full left-0 mb-1 w-52 bg-popover border border-border rounded-md shadow-lg py-1 z-50">
                <button
                  onClick={() => { router.push('/dashboard/profilo'); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                >
                  <Settings className="size-4" /> Profilo e sicurezza
                </button>
                {isSuperadmin && (
                  <button
                    onClick={() => { router.push('/dashboard/admin'); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                  >
                    <Shield className="size-4" /> Pannello Admin
                  </button>
                )}
                <hr className="my-1 border-border" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors"
                >
                  <LogOut className="size-4" /> Esci
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="px-3 py-2 text-[10px] text-muted-foreground/50 text-center border-t border-border">
          v{APP_VERSION} · ultima modifica {LAST_UPDATED}
        </div>
      </aside>
    </>
  );
}
